/**
 * Partes de uma transação financeira: quem comprou, vendeu, doou, emprestou… Cada parte é uma
 * pessoa/empresa já cadastrada (ou criada na hora) com um papel. É o que liga as pontas de um
 * negócio — "Vinicius (comprador) e Rafael (vendedor)" na mesma transação. Espelha o painel de
 * donos do veículo: adicionar por drawer (busca + papel), remover na linha.
 */
import { Trash2, Users2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboBuscaEntidade, type ConfigBusca, type ItemBusca } from '@/components/crud/ComboBuscaEntidade';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatar } from '@/lib/mascaras';

type Parte = {
  owner_tipo: string;
  owner: { tipo: string; id: string; rotulo: string };
  papel: string;
  valor: number | null;
};

const PAPEIS = [
  { valor: 'comprador', rotulo: 'Comprador' },
  { valor: 'vendedor', rotulo: 'Vendedor' },
  { valor: 'doador', rotulo: 'Doador' },
  { valor: 'donatario', rotulo: 'Donatário' },
  { valor: 'credor', rotulo: 'Credor' },
  { valor: 'devedor', rotulo: 'Devedor' },
  { valor: 'locador', rotulo: 'Locador' },
  { valor: 'locatario', rotulo: 'Locatário' },
  { valor: 'prestador', rotulo: 'Prestador' },
  { valor: 'tomador', rotulo: 'Tomador' },
  { valor: 'pagador', rotulo: 'Pagador' },
  { valor: 'recebedor', rotulo: 'Recebedor' },
  { valor: 'interveniente', rotulo: 'Interveniente' },
];
const rotuloPapel = (p: string) => PAPEIS.find((x) => x.valor === p)?.rotulo ?? p;

const BUSCA: Record<'pessoa' | 'empresa', ConfigBusca> = {
  pessoa: {
    endpoint: '/cadastro/pessoas',
    rotulo: (p) => String(p.nome ?? ''),
    sub: (p) => (p.cpf ? formatar(String(p.cpf), 'cpf') : null),
    placeholder: 'Buscar pessoa por nome ou CPF…',
    camposRapido: [
      { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true },
      { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
    ],
  },
  empresa: {
    endpoint: '/cadastro/empresas',
    rotulo: (e) => String(e.razao_social ?? e.nome_fantasia ?? ''),
    sub: (e) => (e.cnpj ? formatar(String(e.cnpj), 'cnpj') : null),
    placeholder: 'Buscar empresa por razão social ou CNPJ…',
    camposRapido: [
      { chave: 'razao_social', rotulo: 'Razão social', tipo: 'texto', obrigatorio: true },
      { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
    ],
  },
};

const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function PainelPartesTransacao({ item }: { item: { id: string } }) {
  const transacaoId = item.id;
  const url = `/cadastro/transacoes/${transacaoId}/partes`;
  const [partes, setPartes] = useState<Parte[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [tipoOwner, setTipoOwner] = useState<'pessoa' | 'empresa'>('pessoa');
  const [alvo, setAlvo] = useState<ItemBusca | null>(null);
  const [papel, setPapel] = useState('comprador');
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    const r = await api.get<Parte[]>(url);
    setCarregando(false);
    if (r.ok) setPartes(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limpar() {
    setAlvo(null);
    setPapel('comprador');
    setValor('');
    setTipoOwner('pessoa');
  }

  async function adicionar(): Promise<boolean> {
    if (!alvo || salvando) return false;
    setSalvando(true);
    const r = await api.post('/cadastro/transacoes/partes', {
      transacao_id: transacaoId,
      owner_tipo: tipoOwner,
      owner_id: alvo.id,
      papel,
      valor: valor.trim() ? valor.trim() : null,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success(`${BUSCA[tipoOwner].rotulo(alvo)} vinculado como ${rotuloPapel(papel)}.`);
    limpar();
    void carregar();
    return true;
  }

  async function remover(p: Parte) {
    const r = await api.del(`${url}/${p.owner_tipo}/${p.owner.id}/${p.papel}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Parte removida.');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users2 className="size-4 text-accent" />
          Partes
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Quem participou do negócio e em que papel. Ligar duas pessoas/empresas aqui as conecta na
          correlação (dossiê e grafo).
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, partes.length === 0, 'Nenhuma parte ainda.') ?? (
          <div className="flex flex-col gap-2">
            {partes.map((p) => (
              <div
                key={`${p.owner.id}/${p.papel}`}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-[15px] text-text">{p.owner.rotulo}</span>
                  <span className="tipo-corpo-pequeno text-text-tertiary">
                    {p.owner.tipo === 'empresa' ? 'Empresa' : 'Pessoa'}
                    {p.valor != null && ` · ${reais(p.valor)}`}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Chip aceso>{rotuloPapel(p.papel)}</Chip>
                  <Button
                    variant="fantasma"
                    size="sm"
                    className="text-danger hover:text-danger"
                    onClick={() => void remover(p)}
                    aria-label="Remover"
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DrawerAdicionar
          titulo="Nova parte"
          descricao="Busque a pessoa/empresa (ou cadastre na hora) e diga o papel no negócio."
          rotuloAbrir="Adicionar parte"
          rotuloSalvar="Vincular"
          podeSalvar={!!alvo}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limpar}
        >
          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Quem</span>
            <div className="flex gap-1 rounded-md bg-surface-2 p-1">
              {(['pessoa', 'empresa'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTipoOwner(t);
                    setAlvo(null);
                  }}
                  className={`flex-1 rounded-sm px-3 py-1.5 text-[13px] font-medium capitalize transition ${
                    tipoOwner === t ? 'bg-surface text-text shadow-[var(--shadow-sm)]' : 'text-text-tertiary'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <ComboBuscaEntidade key={tipoOwner} config={BUSCA[tipoOwner]} onSelecionar={setAlvo} />
          </div>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Papel</span>
            <select
              value={papel}
              onChange={(e) => setPapel(e.target.value)}
              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
            >
              {PAPEIS.map((p) => (
                <option key={p.valor} value={p.valor}>
                  {p.rotulo}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Valor da parte (opcional)</span>
            <input
              type="number"
              step="0.01"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="Ex.: rateio da parte"
              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
            />
          </label>
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}
